import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Trash2, Shield, ShieldCheck, Search, UserX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import {
  getUsers,
  deleteUser,
  updateUser,
  UserData,
  formatCurrency,
  formatDate,
} from '@/features/admin/services/adminService'

export default function AdminUsers() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<UserData[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  useEffect(() => {
    if (search) {
      const filtered = users.filter(
        (u) =>
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.displayName?.toLowerCase().includes(search.toLowerCase())
      )
      setFilteredUsers(filtered)
    } else {
      setFilteredUsers(users)
    }
  }, [search, users])

  const loadUsers = async () => {
    setIsLoading(true)
    const data = await getUsers()
    setUsers(data)
    setFilteredUsers(data)
    setIsLoading(false)
  }

  const handleDelete = async (userId: string) => {
    if (!confirm('¿Eliminar este usuario?')) return
    const result = await deleteUser(userId)
    if (result.success) {
      loadUsers()
    }
  }

  const handleDeleteSelected = async () => {
    if (!confirm(`¿Eliminar ${selectedUsers.size} usuario(s)?`)) return
    setIsDeleting(true)
    for (const userId of selectedUsers) {
      await deleteUser(userId)
    }
    setSelectedUsers(new Set())
    loadUsers()
    setIsDeleting(false)
  }

  const handleTogglePatriarch = async (user: UserData) => {
    await updateUser(user.id, { isPatriarch: !user.isPatriarch })
    loadUsers()
  }

  const toggleSelect = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
    } else {
      newSelected.add(userId)
    }
    setSelectedUsers(newSelected)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.users')}</h1>
        <span className="text-gray-400">{users.length} usuarios</span>
      </div>

      {/* Search and actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email o nombre..."
            className="pl-10"
          />
        </div>
        {selectedUsers.size > 0 && (
          <Button
            variant="destructive"
            onClick={handleDeleteSelected}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {t('admin.deleteSelected')} ({selectedUsers.size})
          </Button>
        )}
      </div>

      {/* Users list */}
      <div className="space-y-2">
        {filteredUsers.map((user) => (
          <Card key={user.id} className="hover:border-zinc-600 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers.has(user.id)}
                    onChange={() => toggleSelect(user.id)}
                    className="rounded"
                  />
                  <div>
                    <p className="font-medium">{user.displayName || 'Sin nombre'}</p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="font-mono text-casino-gold">{formatCurrency(user.balance || 0)}</p>
                    <p className="text-xs text-gray-500">{formatDate(user.createdAt)}</p>
                  </div>

                  <div className="flex items-center gap-1">
                    {user.isAdmin ? (
                      <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded">Admin</span>
                    ) : user.isPatriarch ? (
                      <span className="text-xs bg-purple-900/50 text-purple-400 px-2 py-1 rounded">Patriarca</span>
                    ) : (
                      <span className="text-xs bg-zinc-700 text-gray-400 px-2 py-1 rounded">Miembro</span>
                    )}
                    {user.kycStatus === 'approved' && (
                      <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded">KYC</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {!user.isAdmin && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTogglePatriarch(user)}
                          title={user.isPatriarch ? 'Quitar Patriarca' : 'Hacer Patriarca'}
                        >
                          {user.isPatriarch ? (
                            <ShieldCheck className="w-4 h-4 text-purple-400" />
                          ) : (
                            <Shield className="w-4 h-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredUsers.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-gray-400">
              No se encontraron usuarios
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
